import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Box,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const CONSENT_STORAGE_KEY = "cp_consent_analytics_v1";
const CONSENT_GRANTED = "granted";
const CONSENT_DENIED = "denied";

const persistConsent = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch (error) {
    console.error("Failed to persist consent choice:", error);
  }
};

const readConsent = () => {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === CONSENT_GRANTED || value === CONSENT_DENIED) return value;
    return null;
  } catch (error) {
    console.error("Failed to read consent choice:", error);
    return null;
  }
};

const updateConsentMode = (value) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const analyticsStorage = value === CONSENT_GRANTED ? "granted" : "denied";

  window.gtag("consent", "update", {
    analytics_storage: analyticsStorage,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
};

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [allowAnalytics, setAllowAnalytics] = useState(true);

  useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      setVisible(true);
      return;
    }
    updateConsentMode(stored);
  }, []);

  const handleChoice = useCallback(
    (value) => {
      persistConsent(value);
      updateConsentMode(value);
      setVisible(false);
    },
    []
  );

  return (
    <Dialog
      open={visible}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          px: { xs: 0.5, sm: 1 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, fontWeight: 700 }}>
        Set Up Consent – CDR & Transaction Tree Diagram
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>

          <Typography variant="body2" color="text.secondary">
            This application processes transaction data for visualization and analysis.
            We also use Google Analytics and may show Google AdSense advertising to improve and support the website. Optional analytics and advertising choices are explained in our <MuiLink component={RouterLink} to="/privacy-policy">Privacy Policy</MuiLink> and <MuiLink component={RouterLink} to="/cookie-policy">Cookie Policy</MuiLink>.
          </Typography>

          <Divider />

          {/* Scrollable Detailed Consent Section */}
          <Box
            role="region"
            aria-label="Detailed consent terms"
            tabIndex={0}
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              pr: 1,
            }}
          >
            <Stack spacing={1.5}>

              <Typography sx={{ fontSize: "9px", fontWeight: 600 }}>
                1. Purpose of the Application
              </Typography>

              <Typography sx={{ fontSize: "9px" }}>
                • Analysis of transaction flows and financial relationships.
                <br />
                • Visualization of transaction tree diagrams (root and child nodes).
                <br />
                • Understanding usage behavior and system interactions.
                <br />
                • Performance improvement and debugging.
                <br />
                • Educational and analytical demonstration purposes.
              </Typography>

              <Typography sx={{ fontSize: "9px", fontWeight: 600 }}>
                2. Consumer Data Right (CDR) Consent (If Applicable)
              </Typography>

              <Typography sx={{ fontSize: "9px" }}>
                • You authorize processing of transaction-related data for analysis and visualization.
                <br />
                • Data may include beneficiary name, masked account number, bank name, timestamps, and references.
                <br />
                • Data will not be sold or shared for marketing purposes.
                <br />
                • Processing is strictly limited to operational and analytical purposes.
                <br />
                • You may withdraw consent by discontinuing use of this application.
              </Typography>

              <Typography sx={{ fontSize: "9px", fontWeight: 600 }}>
                3. Google Analytics Usage
              </Typography>

              <Typography sx={{ fontSize: "9px" }}>
                • We use Google Analytics to improve the application experience.
                <br />
                • Analytics may collect anonymized browser, device, and interaction data.
                <br />
                • This helps improve performance and feature usability.
                <br />
                • You may accept or reject analytics tracking.
              </Typography>

              <Typography sx={{ fontSize: "9px", fontWeight: 600 }}>
                4. Data Security & Retention
              </Typography>

              <Typography sx={{ fontSize: "9px" }}>
                • Data is stored securely with restricted access.
                <br />
                • Sensitive information is masked or encrypted where required.
                <br />
                • Logs are retained only as necessary for operational or regulatory needs.
              </Typography>

              <Typography sx={{ fontSize: "9px", fontWeight: 600 }}>
                5. User Responsibilities
              </Typography>

              <Typography sx={{ fontSize: "9px" }}>
                • You confirm you are authorized to provide entered information.
                <br />
                • You understand the application is for analytical and visualization purposes.
                <br />
                • You agree not to misuse the platform for unlawful activities.
              </Typography>

            </Stack>
          </Box>

          <Divider />

          {/* Mandatory Terms Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I have read and understood the above terms and consent to the processing of my data.
              </Typography>
            }
          />

          {/* Optional Analytics Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={allowAnalytics}
                onChange={(e) => setAllowAnalytics(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Allow Google Analytics tracking to improve this application.
              </Typography>
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="outlined"
          color="inherit"
          disabled={!acceptedTerms}
          onClick={() => handleChoice(CONSENT_DENIED)}
        >
          Continue Without Analytics
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={!acceptedTerms}
          onClick={() =>
            handleChoice(allowAnalytics ? CONSENT_GRANTED : CONSENT_DENIED)
          }
        >
          Accept & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
